/**
 * Utility functions for class level formatting and conversion
 */

// Map of abbreviated class levels to formal names
const CLASS_LEVEL_FORMAL = {
  'CP': 'CP',
  'CE1': 'CE1',
  'CE2': 'CE2',
  'CM1': 'CM1',
  'CM2': 'CM2',
  '6e': 'Sixième',
  '5e': 'Cinquième',
  '4e': 'Quatrième',
  '3e': 'Troisième',
  '2nde': 'Seconde',
  '1ère': 'Première',
  '1ere': 'Première',
  'Terminale': 'Terminale',
  'L1': 'L1',
  'L2': 'L2',
  'L3': 'L3',
  'M1': 'M1',
  'M2': 'M2',
  // Also support already formal names
  'Sixième': 'Sixième',
  'Cinquième': 'Cinquième',
  'Quatrième': 'Quatrième',
  'Troisième': 'Troisième',
  'Seconde': 'Seconde',
  'Première': 'Première'
};

// Map of class levels to their numeric IDs
const CLASS_LEVEL_IDS = {
  'CP': 1,
  'CE1': 2,
  'CE2': 3,
  'CM1': 4,
  'CM2': 5,
  '6e': 6, 'Sixième': 6,
  '5e': 7, 'Cinquième': 7,
  '4e': 8, 'Quatrième': 8,
  '3e': 9, 'Troisième': 9,
  '2nde': 10, 'Seconde': 10,
  '1ère': 11, '1ere': 11, 'Première': 11,
  'Terminale': 12,
  'L1': 13,
  'L2': 14,
  'L3': 15,
  'M1': 16,
  'M2': 17
};

/**
 * Convert a class level to its formal name
 * @param {string} level - The class level (e.g., '4e', '3e', 'Seconde')
 * @returns {string} - The formal name (e.g., 'Quatrième', 'Troisième', 'Seconde')
 */
export const formatClassLevel = (level) => {
  if (!level) return '';
  return CLASS_LEVEL_FORMAL[level] || level;
};

/**
 * Get the numeric ID for a class level
 * @param {string} level - The class level
 * @returns {number} - The level ID (6 for 6e, 9 for 3e, etc.)
 */
export const getClassLevelId = (level) => {
  if (!level) return 0;
  return CLASS_LEVEL_IDS[level] || 0;
};

/**
 * Get the class level label from its ID
 * @param {number} id - The level ID
 * @returns {string} - The formal class level name
 */
export const getClassLevelFromId = (id) => {
  const levelMap = {
    1: 'CP',
    2: 'CE1',
    3: 'CE2',
    4: 'CM1',
    5: 'CM2',
    6: 'Sixième',
    7: 'Cinquième',
    8: 'Quatrième',
    9: 'Troisième',
    10: 'Seconde',
    11: 'Première',
    12: 'Terminale',
    13: 'L1',
    14: 'L2',
    15: 'L3',
    16: 'M1',
    17: 'M2'
  };
  return levelMap[id] || '';
};

/**
 * Is this class inside a window? `min`/`max` are class names, null = open end.
 *
 * ⚠ The ids above are the ONLY ordering in the frontend — this is the twin of
 * the backend's `is_in_class_window`, and a second class table is how the two
 * drift apart. An unknown class claims no window rather than defaulting to true:
 * a tool restricted to « up to 6e » must not appear for a class nobody parsed.
 */
export const isClassInWindow = (level, min = null, max = null) => {
  const id = getClassLevelId(level);
  if (!id) return false;
  if (min && id < getClassLevelId(min)) return false;
  if (max && id > getClassLevelId(max)) return false;
  return true;
};

export const isLyceeClass = (level) => isClassInWindow(level, '2nde', 'Terminale');

/**
 * Get all class levels as options for select inputs
 * @returns {Array} - Array of { value, label } objects
 */
export const getClassLevelOptions = () => {
  return [
    { value: 'CP', label: 'CP' },
    { value: 'CE1', label: 'CE1' },
    { value: 'CE2', label: 'CE2' },
    { value: 'CM1', label: 'CM1' },
    { value: 'CM2', label: 'CM2' },
    { value: 'Sixième', label: 'Sixième' },
    { value: 'Cinquième', label: 'Cinquième' },
    { value: 'Quatrième', label: 'Quatrième' },
    { value: 'Troisième', label: 'Troisième' },
    { value: 'Seconde', label: 'Seconde' },
    { value: 'Première', label: 'Première' },
    { value: 'Terminale', label: 'Terminale' },
    { value: 'L1', label: 'L1' },
    { value: 'L2', label: 'L2' },
    { value: 'L3', label: 'L3' },
    { value: 'M1', label: 'M1' },
    { value: 'M2', label: 'M2' }
  ];
};

export default {
  formatClassLevel,
  getClassLevelId,
  getClassLevelFromId,
  getClassLevelOptions
};
